import { Component, Output, EventEmitter } from "@angular/core";
import { OverviewService, PersonInfo } from "./overview.service";
import { ActivityService, PersonActivitySchedule, ActivityTypeInfo, ActivityTypeInfoWithRemark, YearAndMonth } from "../../services/activity.service";
import { WorkplaceService } from "../../services/workplace.service";
import { CrewPermissionsService } from "../../core/crew-permissions.service";
import { Observable } from "rxjs";
import { combineLatest } from "rxjs/observable/combineLatest";
import { switchMap, filter, share, concat } from "rxjs/operators";
import { map, take } from "rxjs/operators";

import dataCore = require("../../core/datacore");
import { of } from "rxjs/observable/of";

@Component({
    selector: "overview-table",
    template: require('./overview-table.component.html'),
    styles: [require('./overview-table.component.css')]
})

export class OverviewTableComponent {

    constructor(
        private overviewService: OverviewService,
        private activityService: ActivityService,
        private workplaceService: WorkplaceService,
        private crewPermissionsService: CrewPermissionsService) { }

    days: Observable<Date[]>;
    workplaceId: Observable<number>;
    workGroupId: Observable<number>;
    yearAndMonth: Observable<YearAndMonth>;
    workplaceName: Observable<string>;
    showBalance: Observable<boolean>;

    currentActivityInfoAndSchedule: Observable<ActivityInfoAndSchedule>;

    isLoading: Observable<boolean>;
    isPermissionWriteOrAdmin: Observable<boolean>;

    @Output() onShowRemark = new EventEmitter<RemarkInfo>();

    ngOnInit(): void {
        this.isLoading = this.overviewService.isProcessing;
        this.isPermissionWriteOrAdmin = this.crewPermissionsService.isPermissionWriteOrAdmin();

        const optionsAreInitialized = this.overviewService.optionsAreInitialized.pipe(
            filter(areInitialized => areInitialized),
            take(1)
        );

        this.workplaceId = this.overviewService.workplaceId;
        this.workGroupId = this.overviewService.workGroupId;
        this.yearAndMonth = this.overviewService.yearAndMonth;
        this.showBalance = this.overviewService.showBalance;

        const showBalanceChange = this.showBalance.pipe(
            take(1),
            concat(this.showBalance.pipe(
                filter(show => show)
            ))
        );

        const allPersonActivitySchedules = combineLatest(
            optionsAreInitialized,
            this.overviewService.shouldRefresh,
            this.workplaceId,
            this.yearAndMonth,
            showBalanceChange,
            this.overviewService.showCrossWorkplaceBalance).pipe(
                switchMap(([_, _2, workplaceId, yearAndMonth, showBalance, showCrossWorkplaceBalance]) => {
                    if (workplaceId && yearAndMonth) {
                        this.overviewService.changeIsProcessing(true);
                        return this.activityService.getPersonActivitySchedules({
                            WorkplaceId: workplaceId,
                            Year: yearAndMonth.Year,
                            Month: yearAndMonth.Month,
                            ShowBalance: showBalance,
                            ShowCrossWorkplaceBalance: showCrossWorkplaceBalance
                        }).pipe(
                            map(response => {
                                this.overviewService.changeIsProcessing(false);
                                return response.body;
                            }));
                    }
                    return of<PersonActivitySchedule[]>([]);
                }),
                share()
            );

        const persons: Observable<PersonInfo[]> = allPersonActivitySchedules.pipe(
            map(personActivitySchedules => personActivitySchedules.map(personActivitySchedule => ({ Id: personActivitySchedule.PersonId, Name: personActivitySchedule.PersonName }))));

        this.overviewService.registerPersonInfoStream(persons);

        this.currentActivityInfoAndSchedule = combineLatest(allPersonActivitySchedules, this.workGroupId, this.overviewService.filterText).pipe(
            map(([personActivitySchedules, workGroupId, filterText]) => {
                if (personActivitySchedules) {
                    const currentWorkGroupPersonActivitySchedules = dataCore.filterBy(
                        personActivitySchedules
                            .filter(personActivitySchedule => !workGroupId || personActivitySchedule.WorkGroupIds
                                .some(workGroupIdForPerson => workGroupIdForPerson === workGroupId)),
                        filterText,
                        "PersonName");

                    const currentActivityTypesUsed = dataCore.distinct(
                        dataCore.selectMany(currentWorkGroupPersonActivitySchedules, personSchedule => personSchedule.ActivitySchedule.map(activity => activity.ActivityTypeInfo).filter(activityTypeInfo => !!activityTypeInfo)),
                        (v1, v2) => v1.ActivityTypeName === v2.ActivityTypeName && v1.ActivityTypeColor === v2.ActivityTypeColor && v1.ActivityTypeTimeNorm === v2.ActivityTypeTimeNorm);

                    return {
                        currentWorkGroupPersonActivitySchedules: currentWorkGroupPersonActivitySchedules,
                        currentActivityTypesUsed: currentActivityTypesUsed
                    };
                }

                return null;
            })
        );

        this.workplaceName = combineLatest(this.workplaceId, this.workplaceService.getWorkplaces()).pipe(
            map(([workplaceId, workplaces]) => workplaces.body.find(workplace => workplace.Id === workplaceId)?.Name));

        this.days = combineLatest(optionsAreInitialized, this.yearAndMonth).pipe(
            map(([_, yearAndMonth]) => this.getDaysInMonth(yearAndMonth.Month, yearAndMonth.Year)));
    }

    private getDaysInMonth(month: number, year: number): Date[] {
        const d = new Date();
        year = year ?? d.getFullYear();
        month = month ?? d.getMonth();
        const numberOfDaysInMonth = new Date(year, month + 1, 0).getDate();

        const result: Date[] = [];
        for (let day = 1; day <= numberOfDaysInMonth; day++) {
            const tmp = new Date(year, month, day);
            result.push(dataCore.subtractTimezoneOffset(tmp));
        }

        return result;
    }

    isWeekend(date: Date): boolean {
        return date.getDay() === 0 || date.getDay() === 6;
    }

    getGoToDateText(d: Date): string {
        return "Go to " + d.toLocaleDateString("da-DK").replace(/\./g, "-");
    }

    showRemark(activityTypeInfoWithRemark: ActivityTypeInfoWithRemark, personId: number, yearAndMonth: YearAndMonth, dayOfMonth: number): void {
        const remark = activityTypeInfoWithRemark?.Remark;
        if (remark) {
            this.onShowRemark.emit({ Remark: remark, PersonId: personId, Year: yearAndMonth.Year, Month: yearAndMonth.Month, DayOfMonth: dayOfMonth });
        }
    }

    getTitle(activityTypeInfoWithRemark: ActivityTypeInfoWithRemark) {
        return activityTypeInfoWithRemark.ActivityTypeInfo?.ActivityTypeName ?? "";
    }
}

interface ActivityInfoAndSchedule {
    currentWorkGroupPersonActivitySchedules: PersonActivitySchedule[];
    currentActivityTypesUsed: ActivityTypeInfo[];
}

export interface RemarkInfo {
    Remark: string;
    PersonId: number;
    Year: number;
    Month: number;
    DayOfMonth: number;
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/@angularclass/hmr-loader!./node_modules/tslint-loader!./client/app/activities/overview/overview-table.component.ts